// UserPasswordComponent.ts
import { CommonModule, NgClass } from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, input} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {FormControlCollectorDirective} from '../../../directives/form-control-collector.directive';
import {UserFormFactory} from '../../../services/user-form.factory';
import {UserFormErrorComponent} from '../components/user-form-error.component';
import {AutoTabIndexDirective} from '@shared/ui';
@Component({
	selector: 'dc-user-form-password',
	styleUrls: ['../user-form.styles.scss'],
	imports: [ReactiveFormsModule, AutoTabIndexDirective, CommonModule, NgClass, FormControlCollectorDirective, TranslateModule, UserFormErrorComponent],
	template: `
		<div class="section-fields-container" [formGroup]="parentForm()">
			<!-- Password -->
			<div class="form-field-container">
				<label for="password">
					{{ 'user.form.section.password.label' | translate }}
				</label>
				<div class="relative">
					<input id="password" [type]="showPassword ? 'text' : 'password'" formControlName="password" [ngClass]="{'border-red-500': isInvalid('password')}" autocomplete="new-password" autoTabIndex />
					<button
						type="button"
						class="absolute inset-y-0 end-0 rounded-e-md pr-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none m-0.5"
						(click)="showPassword = !showPassword"
						aria-label="Toggle password visibility"
					>
						<i class="fa-regular text-lg" [ngClass]="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
					</button>
				</div>
				<!-- Password requirements checklist -->
				<ul class="mt-2 space-y-1 text-sm">
					@for (req of passwordRequirements; track req.key) {
						<li class="flex items-center" [ngClass]="{'text-green-600': req.isValid, 'text-gray-500': !req.isValid}">
							<i class="fa-solid text-xs mr-2 ml-1" [ngClass]="req.isValid ? 'fa-check' : 'fa-circle'"></i>
							{{ req.text | translate: {min: 8} }}
						</li>
					}
				</ul>

				<dc-user-form-error [parentForm]="parentForm()" controlSection="Password" controlName="password" [isInvalidFn]="isInvalid" />
			</div>

			<!-- Confirm Password -->
			<div class="form-field-container">
				<label for="confirmPassword">
					{{ 'user.form.section.password.confirm' | translate }}
				</label>
				<div class="relative ">
					<button
						type="button"
						class="absolute inset-y-0 end-0 rounded-e-md pr-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none m-0.5"
						(click)="showConfirmPassword = !showConfirmPassword"
						aria-label="Toggle confirm password visibility"
					>
						<i class="fa-regular text-lg" [ngClass]="showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
					</button>
					<input
						id="confirmPassword"
						[type]="showConfirmPassword ? 'text' : 'password'"
						formControlName="confirmPassword"
						[ngClass]="{'border-red-500': isInvalid('confirmPassword') || hasMatchError()}"
						autocomplete="new-password"
						autoTabIndex
					/>
				</div>
				<dc-user-form-error [parentForm]="parentForm()" controlSection="Password" controlName="confirmPassword" controlNameLabel="dccomp.user-form.confirmPasswordRequired" [isInvalidFn]="isInvalid" />
			</div>
		</div>
	`,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserFormPasswordComponent {
	private readonly userFormFactory = inject(UserFormFactory);
	protected readonly isInvalid = this.userFormFactory.isInvalidFnFactory<'Password'>({checkDirty: false});
	protected readonly hasMatchError = this.userFormFactory.hasErrorFnFactory('passwordsNotMatch');

	readonly parentForm = input.required<FormGroup>();
	showPassword = false;
	showConfirmPassword = false;

	protected readonly passwordRequirements = this.userFormFactory.PasswordSection.requirements;
}
